/*
  Таблица реестра: строка счёта и заголовки, которыми задают порядок.

  🔴 Сортировка добавлена 14.09.2026 по указанию владельца. Конституция её
  предусматривала (§3.3.6), но код сознательно не делал: порядок строк задаёт
  триаж - сначала то, что горит, - и сортировка позволяет этот порядок потерять.
  Поэтому она осталась ЯВНЫМ выбором: без нажатия порядок прежний, выбранный
  виден стрелкой, а возврат к важности стоит рядом с таблицей.

  Сортируются три колонки, и ровно те, что назвал владелец: счёт, взыскание,
  давность. Остальные - объяснение решения, стадия, адрес - упорядочивать нечем:
  по ним человек не ищет, а читает.

  Компонент ничего не грузит: порядок держит страница, она же ходит на сервер.
*/

import { Link } from 'react-router-dom'

import { LIMITATION_TITLES, ROUTE_TITLES, date, money } from '../api/format.js'
import { Check, Tag } from './Common.jsx'
import PickShown from './PickShown.jsx'
import RegistryDebtor from './RegistryDebtor.jsx'

const ARROWS = { asc: '↑', desc: '↓' }

/*
  `pick` - выбор строк для действий полосы под таблицей (16.10.2026): удалить,
  сменить стадию, открыть конвейер. Без него таблица прежняя - у того, кому
  действия не положены, и колонки флажков нет. «Выбрать все» в шапке берёт
  ровно видимую страницу, как в конвейере (решение владельца 09.09.2026); весь
  отбор целиком - кнопка «Выбрать весь отбор» над таблицей (`PickWhole`, решение
  владельца 23.10.2026).
*/
export default function RegistryTable({ rows, sort, direction, onSort, pick }) {
  const header = { sort, direction, onSort }
  const shownPicked = pick ? rows.filter((row) => pick.chosen.has(row.account_id)).length : 0
  return (
    <>
      {pick ? (
        <PickShown
          count={rows.length}
          picked={shownPicked}
          onToggle={(on) => pick.toggleAll(rows, on)}
        />
      ) : null}
      <table className={`table table--fixed table--registry${pick ? ' table--picking' : ''}`}>
        <RegistryHead
          header={header}
          pick={pick}
          rows={rows}
          shownPicked={shownPicked}
        />
        <tbody>
          {rows.map((row) => (
            <Row key={row.account_id} row={row} pick={pick} />
          ))}
        </tbody>
      </table>
    </>
  )
}

function RegistryHead({ header, pick, rows, shownPicked }) {
  return (
    <thead>
        <tr>
          {pick ? (
            <th className="table__control">
              <Check
                checked={shownPicked === rows.length}
                indeterminate={shownPicked > 0 && shownPicked < rows.length}
                onChange={(on) => pick.toggleAll(rows, on)}
                title="Выбрать счета этой страницы"
              />
            </th>
          ) : null}
          <SortHeader field="number" title="Счёт" {...header} />
          <th>Должник</th>
          <th>Адрес</th>
          <SortHeader field="amount" title="К взысканию" numeric {...header} />
          <SortHeader field="limitation" title="Давность" {...header} />
          <th>Стадия</th>
          <th>Решение</th>
        </tr>
    </thead>
  )
}

/*
  Заголовок, которым задают порядок. `aria-sort` обязателен: стрелку читалка
  экрана не произносит, и без него человек не узнает, что список упорядочен.

  `no-hover-lift`: разметкой это кнопка, но для человека - заголовок колонки, а
  не действие. Подъём заставлял бы строку заголовков прыгать под курсором
  (скилл `frontend`, правило 6б).
*/
function SortHeader({ field, title, sort, direction, onSort, numeric = false }) {
  const active = sort === field
  return (
    <th
      className={numeric ? 'table__num' : undefined}
      aria-sort={active ? (direction === 'desc' ? 'descending' : 'ascending') : 'none'}
    >
      <button className="table__sort no-hover-lift" type="button" onClick={() => onSort(field)}>
        {title}
        {active ? <span aria-hidden="true"> {ARROWS[direction] ?? ARROWS.asc}</span> : null}
      </button>
    </th>
  )
}

function Row({ row, pick }) {
  const triage = row.triage
  const burning = triage.limitation_band === 'expiring_soon'
  return (
    // `data-label` - подпись колонки для узкого экрана: там строка
    // разворачивается в карточку, заголовки таблицы не видны, и без подписи
    // человек не знает, что за число перед ним (`styles.adaptive.css`).
    // У номера счёта подписи нет намеренно: он заголовок карточки и читается
    // сам по себе.
    <tr>
      {pick ? (
        <td className="table__control">
          <Check
            checked={pick.chosen.has(row.account_id)}
            onChange={() => pick.toggle(row)}
            title={`Выбрать счёт ${row.number}`}
          />
        </td>
      ) : null}
      <td>
        <Link to={`/accounts/${row.account_id}`}>№ {row.number}</Link>
      </td>
      {/* Колонка «Должник» - сразу за счётом (решение владельца 19.10.2026,
          макет «3-реестр-должник-А»): счёт и его должник читаются парой. */}
      <td className="registry-who" data-label="Должник">
        <RegistryDebtor debtor={row.debtor} />
      </td>
      <td data-label="Адрес">{row.address}</td>
      <td className="table__num" data-label="К взысканию">
        {money(triage.claim_amount)}
      </td>
      {/*
        Сумма и дата стоят в два этажа, а не одной меткой в строку. Метка
        «горит 18 600 ₽ · 11.09.2026» занимала сто восемьдесят два пикселя и
        забирала пятую часть ширины таблицы у объяснения решения, которому её
        не хватало (замер 09.09.2026). Разделённая надвое, колонка укладывается
        в двенадцать процентов, и высота строки падает с четырёх строк текста
        до трёх.
      */}
      <td data-label="Давность">
        {burning ? (
          <>
            <Tag kind="burn">горит {money(triage.burning_amount)}</Tag>
            {triage.burning_date ? <div className="muted">до {date(triage.burning_date)}</div> : null}
          </>
        ) : (
          <span className="muted">{LIMITATION_TITLES[triage.limitation_band]}</span>
        )}
      </td>
      <td data-label="Стадия">{row.stage_title}</td>
      <td className="table__block" data-label="Решение">
        <RouteTag route={triage.route} />
        <div className="muted">{triage.verdict.explanation}</div>
      </td>
    </tr>
  )
}

function RouteTag({ route }) {
  const kind = route === 'court' ? 'calm' : route === 'manual_review' ? 'warn' : 'muted'
  return <Tag kind={kind}>{ROUTE_TITLES[route]}</Tag>
}
